import { useEffect, useMemo, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Tag from '../reusable/Tag';
import useUserDetails from '../hooks/useUserDetails';
import { getCachedStates, getStates, State } from '../../services/eoaConfigServices';
import { AcademicsForm } from './academicsForm';
import { DistrictInformationForm } from './districtInformationForm';
import { EoaaInfo2Form, EligibilitySubject } from './eoaaInfo2Form';
import { EoaaInfoForm } from './eoaaInfoForm';
import { GradingForm, GRADING_SYSTEM_OPTIONS } from './gradingForm';
import { REGIONAL_AREA_OPTIONS, SchoolInfoForm, SchoolInfoRow } from './schoolInfoForm';
import { ConfigurationStepId, ConfigurationStepStatus } from './eoaConfigurationSteps';
import { schoolStatus } from './configurationValidity';

interface ReviewAndSubmitProps {
  districtInformation: DistrictInformationForm;
  academics: AcademicsForm;
  grading: GradingForm;
  eoaaInfo: EoaaInfoForm;
  eoaaInfo2: EoaaInfo2Form;
  schoolInfo: SchoolInfoForm;
  surveyLanguages: { id: string | number; name: string }[];
  statuses: Record<ConfigurationStepId, ConfigurationStepStatus>;
  onEdit: (stepId: ConfigurationStepId) => void;
}

const emptyValue = 'Not provided yet';
const valueOrEmpty = (value: string | undefined | null) => value?.trim() || emptyValue;
const yesNo = (value: 'yes' | 'no' | '') => (value === 'yes' ? 'Yes' : value === 'no' ? 'No' : emptyValue);
const people = (entries: { name: string; email: string }[]) =>
  entries
    .filter((entry) => entry.name.trim() || entry.email.trim())
    .map((entry) =>
      entry.name.trim() && entry.email.trim()
        ? `${entry.name.trim()} — ${entry.email.trim()}`
        : entry.name.trim() || entry.email.trim()
    )
    .join(', ') || emptyValue;

const ReviewCard = ({
  title,
  stepId,
  status,
  onEdit,
  children,
}: {
  title: string;
  stepId: ConfigurationStepId;
  status: ConfigurationStepStatus;
  onEdit: (stepId: ConfigurationStepId) => void;
  children: React.ReactNode;
}) => (
  <section className="eoa-review-card">
    <div className="eoa-review-card-header">
      <div className="eoa-review-title-group">
        <h3>{title}</h3>
        <Tag
          type={status === 'completed' ? 'completed' : status === 'in-progress' ? 'progress' : 'pending'}
          text={status === 'completed' ? 'Completed' : status === 'in-progress' ? 'In progress' : 'Not started'}
        />
      </div>
      <div className="eoa-review-card-actions">
        <button type="button" className="eoa-review-edit" onClick={() => onEdit(stepId)}>
          Edit
        </button>
      </div>
    </div>
    {children}
  </section>
);

const ReviewRows = ({ rows }: { rows: Array<[string, string]> }) => (
  <dl className="eoa-review-rows">
    {rows.map(([label, value]) => (
      <div key={label} className="eoa-review-row">
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    ))}
  </dl>
);

const schoolDetails = (school: SchoolInfoRow): Array<[string, string]> => [
  ['Primary school on the EOAA', yesNo(school.isPrimary)],
  ['School number', valueOrEmpty(school.schoolNumber)],
  ['School type', valueOrEmpty(school.schoolType)],
  ['Graduation status available', yesNo(school.graduationStatus)],
  ['SAT required', yesNo(school.requiresSat)],
  ['ACT required', yesNo(school.requiresAct)],
  [
    'CTE courses',
    school.hasCteCourses === 'yes'
      ? school.hasDetailedCteClusters === 'yes'
        ? 'Yes — detailed CTE clusters available'
        : 'Yes'
      : yesNo(school.hasCteCourses),
  ],
  ['High wage threshold', valueOrEmpty(school.highWageThreshold)],
  ['High growth threshold', valueOrEmpty(school.highGrowthThreshold)],
  [
    'Regional area',
    [REGIONAL_AREA_OPTIONS.find((option) => option.value === school.regionalAreaType)?.label, school.regionalAreaTitle]
      .filter(Boolean)
      .join(' — ') || emptyValue,
  ],
];

const subjects: EligibilitySubject[] = ['English', 'Math', 'Social Science', 'Science', 'Elective', 'World Language'];

const ReviewSchool = ({ school }: { school: SchoolInfoRow }) => {
  const [isOpen, setIsOpen] = useState(true);
  const status = schoolStatus(school);
  const schoolMeta = [school.isPrimary === 'yes' ? 'Primary' : '', school.schoolType.trim()]
    .filter(Boolean)
    .join(' · ');

  return (
    <details className="eoa-review-school" open={isOpen} onToggle={(event) => setIsOpen(event.currentTarget.open)}>
      <summary>
        <span className="eoa-review-school-chevron" aria-hidden="true">
          {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </span>
        <span className="eoa-review-school-name">{valueOrEmpty(school.name)}</span>
        {schoolMeta && <span className="eoa-review-school-meta">{schoolMeta}</span>}
        <Tag
          className="eoa-review-school-status"
          size="small"
          type={status === 'completed' ? 'completed' : status === 'in-progress' ? 'progress' : 'pending'}
          text={status === 'completed' ? 'Completed' : status === 'in-progress' ? 'In progress' : 'Not started'}
        />
      </summary>
      <ReviewRows rows={schoolDetails(school)} />
    </details>
  );
};

const ReviewAndSubmit = ({
  districtInformation,
  academics,
  grading,
  eoaaInfo,
  eoaaInfo2,
  schoolInfo,
  surveyLanguages,
  statuses,
  onEdit,
}: ReviewAndSubmitProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const [states, setStates] = useState<State[]>(getCachedStates);

  useEffect(() => {
    if (!userDetails?.access_token) return;
    void getStates(userDetails.eoaId ?? '', userDetails.access_token)
      .then((response) => setStates(response.data))
      .catch(() => setStates([]));
  }, [userDetails?.access_token, userDetails?.eoaId]);

  const stateName = useMemo(
    () => states.find((state) => state.state_id === districtInformation.stateId)?.name ?? '',
    [districtInformation.stateId, states]
  );
  const aliases = academics.aliasMappings
    .map((mapping) => {
      const values = mapping.aliases.map((alias) => alias.value.trim()).filter(Boolean);
      return mapping.periodName.trim() && values.length
        ? `${values.join(', ')} maps to ${mapping.periodName.trim()}`
        : '';
    })
    .filter(Boolean)
    .join(', ');
  const gradingSystem = GRADING_SYSTEM_OPTIONS.find((option) => option.value === grading.gradingSystem)?.label;
  const selectedLanguages = eoaaInfo.additionalLanguageIds
    .map((id) => surveyLanguages.find((language) => String(language.id) === String(id))?.name)
    .filter((name): name is string => Boolean(name));
  const columnNames = [
    'High school diploma',
    'EOAA college eligibility',
    eoaaInfo2.iheNames[0] || 'School #1',
    eoaaInfo2.iheNames[1] || 'School #2',
  ];
  const columns = ['diploma', 'eoaa', 'ihe-0', 'ihe-1'] as const;
  const eoaaInfoStatus: ConfigurationStepStatus =
    statuses['eoaa-info-1'] === 'completed' && statuses['eoaa-info-2'] === 'completed'
      ? 'completed'
      : statuses['eoaa-info-1'] === 'not-started' && statuses['eoaa-info-2'] === 'not-started'
        ? 'not-started'
        : 'in-progress';

  return (
    <div className="eoa-review-cards">
      <ReviewCard
        title="1. District information"
        stepId="district-information"
        status={statuses['district-information']}
        onEdit={onEdit}
      >
        <ReviewRows
          rows={[
            ['State', valueOrEmpty(stateName)],
            ['District name', valueOrEmpty(districtInformation.districtName)],
            [
              'Other names / aliases in the data',
              valueOrEmpty(districtInformation.districtAliases.replace(/\n/g, '; ')),
            ],
            ['SIS', valueOrEmpty(districtInformation.sis)],
            ['LMS', valueOrEmpty(districtInformation.lms)],
            ['DSA signed', yesNo(districtInformation.dsaSigned)],
          ]}
        />
      </ReviewCard>
      <ReviewCard title="2. Academics" stepId="academics" status={statuses.academics} onEdit={onEdit}>
        <ReviewRows
          rows={[
            ['Academic term count', valueOrEmpty(academics.termCount)],
            [
              'Academic period names',
              academics.periodNames
                .map((period) => period.name.trim())
                .filter(Boolean)
                .join(', ') || emptyValue,
            ],
            ['Period aliases', aliases || emptyValue],
            ['Cross-school enrollment', valueOrEmpty(academics.crossSchoolEnrollment)],
            ['Student transfers', valueOrEmpty(academics.studentTransfers)],
            ['Dual-enrollment courses', valueOrEmpty(academics.dualEnrollment)],
          ]}
        />
      </ReviewCard>
      <ReviewCard title="3. Grading" stepId="grading" status={statuses.grading} onEdit={onEdit}>
        <ReviewRows
          rows={[
            ['Default credits per course', valueOrEmpty(grading.defaultCredits)],
            ['Grading system', gradingSystem || emptyValue],
            [
              'Failure threshold',
              [grading.failingNumber, grading.failingLetter].filter(Boolean).join(' / ') || emptyValue,
            ],
            ['Credit hours to Carnegie Units', valueOrEmpty(grading.carnegieUnits)],
            [
              'Number to letter grade mapping',
              grading.gradeMaps
                .filter((row) => row.letter.trim() || row.number.trim())
                .map((row) => `${row.letter} = ${row.number}`)
                .join(', ') || emptyValue,
            ],
            ['Different credit systems on transcripts', valueOrEmpty(grading.multipleCreditSystems)],
          ]}
        />
      </ReviewCard>
      <ReviewCard title="4. EOAA information" stepId="eoaa-info-1" status={eoaaInfoStatus} onEdit={onEdit}>
        <ReviewRows
          rows={[
            ['Graduating year of EOAA cohort', valueOrEmpty(eoaaInfo.yearEnding)],
            ['Date of student journey workshop', valueOrEmpty(eoaaInfo.workshopDate)],
            ['Minimum cell size for PII suppression', valueOrEmpty(eoaaInfo.minCellSize)],
            ['District data leads', people(eoaaInfo.districtDataLeads)],
            ['Lead facilitator(s)', people(eoaaInfo.leadFacilitators)],
            ['Survey languages', ['English', ...selectedLanguages].join(', ')],
            ['College eligibility basis (IHEs)', eoaaInfo2.iheNames.filter(Boolean).join(', ') || emptyValue],
          ]}
        />
        <div className="eoa-review-table-wrap">
          <h4>College eligibility — credits required by subject</h4>
          <table className="eoa-review-table">
            <thead>
              <tr>
                <th>Subject</th>
                {columnNames.map((name) => (
                  <th key={name}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subjects.map((subject) => (
                <tr key={subject}>
                  <th scope="row">{subject}</th>
                  {columns.map((column) => {
                    const cell = eoaaInfo2.cells[column][subject];
                    const display =
                      cell.credits && cell.note ? `${cell.credits} (${cell.note})` : cell.credits || cell.note;
                    return <td key={column}>{display || '—'}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReviewCard>
      <ReviewCard
        title={`5. Schools (${schoolInfo.schools.length})`}
        stepId="school-info"
        status={statuses['school-info']}
        onEdit={onEdit}
      >
        <div className="eoa-review-schools">
          {schoolInfo.schools.length ? (
            schoolInfo.schools.map((school) => <ReviewSchool key={school.id} school={school} />)
          ) : (
            <p className="eoa-review-empty">No schools have been added.</p>
          )}
        </div>
      </ReviewCard>
    </div>
  );
};

export default ReviewAndSubmit;
